import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Package, 
  Truck, 
  AlertTriangle, 
  CheckCircle2,
  User,
  Calendar,
  FileText,
  Building2
} from 'lucide-react';

function GRNViewDialog({ open, onOpenChange, grn }) {
  if (!grn) return null;
  
  const hasExceptions = grn.has_shortage || grn.has_alternate_stock || grn.has_damaged_stock;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-3">
            <Package className="w-5 h-5 text-blue-600" />
            GRN Details - {grn.grn_number}
            <Badge className={
              grn.status === 'completed' ? 'bg-green-100 text-green-800' :
              grn.status === 'draft' ? 'bg-gray-100 text-gray-800' :
              'bg-red-100 text-red-800'
            }>
              {(grn.status || 'completed').toUpperCase()}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto py-4 space-y-6">
          {/* Header Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                <FileText size={16} />
                GRN Information
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">GRN Number:</span>
                  <span className="font-semibold">{grn.grn_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">PO Number:</span>
                  <span className="font-mono">{grn.po_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Received Date:</span>
                  <span>{new Date(grn.received_date).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Received By:</span>
                  <span>{grn.received_by_name || grn.received_by}</span>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Building2 size={16} />
                Supplier / Retailer
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Supplier:</span>
                  <span className="font-semibold">{grn.supplier_name}</span>
                </div>
                {grn.retailer_name && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Retailer:</span>
                    <span>{grn.retailer_name}</span>
                  </div>
                )}
                {grn.franchise_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Franchise ID:</span>
                    <span className="font-mono text-xs">{grn.franchise_id}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Quantity Summary */}
          <div className="grid grid-cols-5 gap-3">
            <div className="p-3 bg-blue-50 rounded-lg text-center border border-blue-200">
              <p className="text-xs text-blue-600 font-medium">Ordered</p>
              <p className="text-xl font-bold text-blue-800">{grn.total_ordered || 0}</p>
            </div>
            <div className="p-3 bg-indigo-50 rounded-lg text-center border border-indigo-200">
              <p className="text-xs text-indigo-600 font-medium">Received</p>
              <p className="text-xl font-bold text-indigo-800">{grn.total_received || 0}</p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg text-center border border-yellow-200">
              <p className="text-xs text-yellow-600 font-medium">Shortage</p>
              <p className={`text-xl font-bold ${grn.total_shortage > 0 ? 'text-yellow-800' : 'text-gray-400'}`}>
                {grn.total_shortage || 0}
              </p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg text-center border border-red-200">
              <p className="text-xs text-red-600 font-medium">Damaged</p>
              <p className={`text-xl font-bold ${grn.total_damaged > 0 ? 'text-red-800' : 'text-gray-400'}`}>
                {grn.total_damaged || 0}
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg text-center border border-green-200">
              <p className="text-xs text-green-600 font-medium">Accepted</p>
              <p className="text-xl font-bold text-green-800">{grn.total_accepted || 0}</p>
            </div>
          </div>
          
          {/* Exception Flags */}
          {hasExceptions && (
            <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-800">Exception Flags:</span>
              {grn.has_shortage && (
                <Badge className="bg-yellow-100 text-yellow-800">Shortage Received</Badge>
              )}
              {grn.has_damaged_stock && (
                <Badge className="bg-red-100 text-red-800">Damaged Stock</Badge>
              )}
              {grn.has_alternate_stock && (
                <Badge className="bg-blue-100 text-blue-800">Alternate Stock</Badge>
              )}
            </div>
          )}
          
          {/* Items Table */}
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Package size={16} />
              Received Items
            </h3>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Product</th>
                    <th className="px-3 py-3 text-center font-semibold">Ordered</th>
                    <th className="px-3 py-3 text-center font-semibold">Received</th>
                    <th className="px-3 py-3 text-center font-semibold">Shortage</th>
                    <th className="px-3 py-3 text-center font-semibold">Damaged</th>
                    <th className="px-3 py-3 text-center font-semibold">Accepted</th>
                    <th className="px-3 py-3 text-center font-semibold">Batch #</th>
                    <th className="px-3 py-3 text-center font-semibold">Expiry</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(grn.items || []).map((item, idx) => (
                    <tr key={item.product_id ? `${item.product_id}-${idx}` : `grn-row-${idx}`} className={`hover:bg-gray-50 ${item.is_alternate ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{item.product_name}</p>
                          {item.is_alternate && (
                            <div className="mt-1">
                              <Badge variant="outline" className="text-blue-600 text-xs">Alternate</Badge>
                              {item.alternate_remarks && (
                                <p className="text-xs text-gray-500 mt-1">{item.alternate_remarks}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center text-gray-600">
                        {item.is_alternate ? '-' : item.ordered_quantity}
                      </td>
                      <td className="px-3 py-3 text-center text-blue-600 font-medium">
                        {item.received_quantity}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={item.shortage_quantity > 0 ? 'text-yellow-600 font-semibold' : 'text-gray-400'}>
                          {item.shortage_quantity || 0}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={item.damaged_quantity > 0 ? 'text-red-600 font-semibold' : 'text-gray-400'}>
                          {item.damaged_quantity || 0}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="font-bold text-green-600">
                          {item.accepted_quantity || item.received_quantity}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center text-xs text-gray-500">
                        {item.batch_number || '-'}
                      </td>
                      <td className="px-3 py-3 text-center text-xs text-gray-500">
                        {item.expiry_date ? new Date(item.expiry_date).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        }) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Disclaimer Remarks */}
          {grn.disclaimer_remarks && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h3 className="text-sm font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                <AlertTriangle size={16} />
                Disclaimer / Remarks
              </h3>
              <p className="text-sm text-yellow-700">{grn.disclaimer_remarks}</p>
            </div>
          )}
          
          {/* Notes */}
          {grn.notes && (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <FileText size={16} />
                Additional Notes
              </h3>
              <p className="text-sm text-gray-600">{grn.notes}</p>
            </div>
          )}
          
          {/* Timestamps */}
          <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t">
            <span>Created: {new Date(grn.created_at).toLocaleString('en-IN')}</span>
            {grn.updated_at && grn.updated_at !== grn.created_at && (
              <span>Last Updated: {new Date(grn.updated_at).toLocaleString('en-IN')}</span>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 flex justify-end pt-4 border-t">
          <Button onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default GRNViewDialog;
